import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthProvider';
import { apiUrl } from '../../lib/api';

const MONO = "'JetBrains Mono', monospace";
const ORBITRON = "'Orbitron', monospace";
const TEAL = '#00FFE5';
const TEAL_DIM = 'rgba(0,255,229,0.6)';
const TEAL_FAINT = 'rgba(0,255,229,0.08)';
const TEAL_BORDER = 'rgba(0,255,229,0.2)';
const BG = '#080810';
const CARD_BG = 'rgba(13,13,26,0.95)';
const MUTED = 'rgba(0,255,229,0.45)';
const WHITE = '#E8E8F0';
const RED = '#FF4444';
const GREEN = '#2DC896';

// ─── Inline Auth Modal ───────────────────────────────────────────────────────
function AuthModal({ onSuccess }) {
  const { signIn, signUp } = useAuth();
  const [mode, setMode] = useState('login'); // 'login' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');

  const inputStyle = {
    width: '100%',
    background: 'rgba(0,255,229,0.04)',
    border: `1px solid ${TEAL_BORDER}`,
    borderRadius: 4,
    padding: '10px 14px',
    fontFamily: MONO,
    fontSize: '0.85rem',
    color: WHITE,
    outline: 'none',
    boxSizing: 'border-box',
  };

  const btnStyle = {
    width: '100%',
    background: TEAL,
    color: '#080810',
    border: 'none',
    borderRadius: 4,
    padding: '11px 0',
    fontFamily: ORBITRON,
    fontSize: '0.75rem',
    fontWeight: 700,
    letterSpacing: '0.12em',
    cursor: 'pointer',
    marginTop: 8,
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    setError('');
    setStatus('');
    try {
      const result = mode === 'login'
        ? await signIn(email, password)
        : await signUp(email, password, { emailRedirectTo: window.location.href });
      if (result?.error) {
        setError(result?.error?.message || 'Authentication failed.');
      } else if (result?.data?.session && result?.data?.user) {
        onSuccess(result?.data?.user);
      } else if (mode === 'signup' && result?.data?.user) {
        setStatus('Check your email to confirm this account, then reopen this invitation link.');
      } else {
        setError('Authentication failed. Please try again.');
      }
    } catch (err) {
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      background: CARD_BG,
      border: `1px solid ${TEAL_BORDER}`,
      borderRadius: 8,
      padding: '32px 28px',
      width: '100%',
      maxWidth: 400,
    }}>
      <div style={{ fontFamily: ORBITRON, fontSize: '0.8rem', fontWeight: 700, color: TEAL, letterSpacing: '0.15em', marginBottom: 6 }}>
        {mode === 'login' ? 'SIGN IN TO CONTINUE' : 'CREATE ACCOUNT'}
      </div>
      <div style={{ fontFamily: MONO, fontSize: '0.72rem', color: MUTED, marginBottom: 24 }}>
        You must be logged in to accept this workspace invitation.
      </div>
      <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <input
          type="email"
          placeholder="Email address"
          value={email}
          onChange={e => setEmail(e?.target?.value)}
          required
          style={inputStyle}
          autoFocus
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={e => setPassword(e?.target?.value)}
          required
          style={inputStyle}
        />
        {error && (
          <div style={{ fontFamily: MONO, fontSize: '0.72rem', color: RED, padding: '8px 12px', background: 'rgba(255,68,68,0.08)', border: `1px solid rgba(255,68,68,0.2)`, borderRadius: 4 }}>
            {error}
          </div>
        )}
        {status && (
          <div style={{ fontFamily: MONO, fontSize: '0.72rem', color: GREEN, padding: '8px 12px', background: 'rgba(45,200,150,0.08)', border: '1px solid rgba(45,200,150,0.2)', borderRadius: 4 }}>
            {status}
          </div>
        )}
        <button type="submit" disabled={loading} style={{ ...btnStyle, opacity: loading ? 0.6 : 1 }}>
          {loading ? 'AUTHENTICATING...' : mode === 'login' ? 'SIGN IN' : 'CREATE ACCOUNT'}
        </button>
      </form>
      <div style={{ marginTop: 16, textAlign: 'center' }}>
        <button
          onClick={() => { setMode(mode === 'login' ? 'signup' : 'login'); setError(''); setStatus(''); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: MONO, fontSize: '0.72rem', color: TEAL_DIM, textDecoration: 'underline' }}
        >
          {mode === 'login' ? "Don't have an account? Sign up" : 'Already have an account? Sign in'}
        </button>
      </div>
    </div>
  );
}

// ─── Main Join Page ───────────────────────────────────────────────────────────
export default function JoinPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, loading: authLoading } = useAuth();

  const token = searchParams?.get('token');

  const [phase, setPhase] = useState('init'); // 'init' | 'auth' | 'joining' | 'success' | 'error'
  const [errorMsg, setErrorMsg] = useState('');
  const [countdown, setCountdown] = useState(3);

  const getJwt = useCallback(async () => {
    const { data } = await supabase?.auth?.getSession();
    return data?.session?.access_token || null;
  }, []);

  const doJoin = useCallback(async () => {
    setPhase('joining');
    try {
      const jwt = await getJwt();
      const res = await fetch(apiUrl('/api/workspace/join'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}),
        },
        body: JSON.stringify({ token }),
      });

      let body;
      try { body = await res?.json(); } catch { body = {}; }

      if (res?.ok) {
        setPhase('success');
      } else {
        setErrorMsg(body?.message || body?.error || `Error ${res?.status}: Failed to join workspace.`);
        setPhase('error');
      }
    } catch (err) {
      setErrorMsg('Network error. Please try again.');
      setPhase('error');
    }
  }, [token, getJwt]);

  // Once auth is resolved, decide what to do
  useEffect(() => {
    if (authLoading) return;
    if (phase !== 'init') return;

    if (!token) {
      setErrorMsg('No invitation token found in the URL.');
      setPhase('error');
      return;
    }

    if (user) {
      doJoin();
    } else {
      setPhase('auth');
    }
  }, [authLoading, user, token, phase, doJoin]);

  // Countdown + redirect on success
  useEffect(() => {
    if (phase !== 'success') return;
    if (countdown <= 0) {
      navigate('/command-center-ai-execution-interface', { replace: true });
      return;
    }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [phase, countdown, navigate]);

  const handleAuthSuccess = () => {
    doJoin();
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh',
      background: BG,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      fontFamily: MONO,
    }}>
      {/* Header */}
      <div style={{ marginBottom: 40, textAlign: 'center' }}>
        <div style={{ fontFamily: ORBITRON, fontSize: '1.1rem', fontWeight: 700, color: TEAL, letterSpacing: '0.2em', marginBottom: 6 }}>
          RECALLCORE
        </div>
        <div style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED, letterSpacing: '0.1em' }}>
          WORKSPACE INVITATION
        </div>
      </div>
      {/* Content card */}
      <div style={{ width: '100%', maxWidth: 440, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0 }}>

        {/* INIT / AUTH LOADING */}
        {(phase === 'init' || authLoading) && (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontFamily: MONO, fontSize: '0.8rem', color: TEAL_DIM, letterSpacing: '0.1em' }}>
              INITIALIZING...
            </div>
            <div style={{ marginTop: 16, display: 'flex', gap: 6, justifyContent: 'center' }}>
              {[0,1,2]?.map(i => (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: '50%', background: TEAL,
                  animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          </div>
        )}

        {/* AUTH MODAL */}
        {phase === 'auth' && (
          <AuthModal onSuccess={handleAuthSuccess} />
        )}

        {/* JOINING */}
        {phase === 'joining' && (
          <div style={{
            background: CARD_BG,
            border: `1px solid ${TEAL_BORDER}`,
            borderRadius: 8,
            padding: '40px 28px',
            width: '100%',
            textAlign: 'center',
          }}>
            <div style={{ fontFamily: ORBITRON, fontSize: '0.8rem', color: TEAL, letterSpacing: '0.15em', marginBottom: 12 }}>
              JOINING WORKSPACE
            </div>
            <div style={{ fontFamily: MONO, fontSize: '0.72rem', color: MUTED }}>
              Processing your invitation...
            </div>
            <div style={{ marginTop: 20, display: 'flex', gap: 6, justifyContent: 'center' }}>
              {[0,1,2]?.map(i => (
                <div key={i} style={{
                  width: 6, height: 6, borderRadius: '50%', background: TEAL,
                  animation: `pulse 1.2s ease-in-out ${i * 0.2}s infinite`,
                }} />
              ))}
            </div>
          </div>
        )}

        {/* SUCCESS */}
        {phase === 'success' && (
          <div style={{
            background: CARD_BG,
            border: `1px solid rgba(45,200,150,0.3)`,
            borderRadius: 8,
            padding: '40px 28px',
            width: '100%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '2rem', marginBottom: 16 }}>✓</div>
            <div style={{ fontFamily: ORBITRON, fontSize: '0.85rem', fontWeight: 700, color: GREEN, letterSpacing: '0.12em', marginBottom: 16 }}>
              ACCESS GRANTED
            </div>
            <div style={{ fontFamily: MONO, fontSize: '0.85rem', color: WHITE, lineHeight: 1.6, marginBottom: 24 }}>
              Welcome to the workspace. You now have access to RecallCore.
            </div>
            <div style={{ fontFamily: MONO, fontSize: '0.7rem', color: MUTED }}>
              Redirecting in {countdown}s...
            </div>
            <div style={{ marginTop: 16, height: 2, background: TEAL_FAINT, borderRadius: 1, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                background: TEAL,
                borderRadius: 1,
                width: `${((3 - countdown) / 3) * 100}%`,
                transition: 'width 1s linear',
              }} />
            </div>
          </div>
        )}

        {/* ERROR */}
        {phase === 'error' && (
          <div style={{
            background: CARD_BG,
            border: `1px solid rgba(255,68,68,0.25)`,
            borderRadius: 8,
            padding: '40px 28px',
            width: '100%',
            textAlign: 'center',
          }}>
            <div style={{ fontSize: '1.8rem', marginBottom: 16 }}>⚠</div>
            <div style={{ fontFamily: ORBITRON, fontSize: '0.8rem', fontWeight: 700, color: RED, letterSpacing: '0.12em', marginBottom: 16 }}>
              INVITATION ERROR
            </div>
            <div style={{ fontFamily: MONO, fontSize: '0.82rem', color: WHITE, lineHeight: 1.6, marginBottom: 24 }}>
              {errorMsg}
            </div>
            <button
              onClick={() => navigate('/', { replace: true })}
              style={{
                background: 'none',
                border: `1px solid ${TEAL_BORDER}`,
                borderRadius: 4,
                padding: '9px 20px',
                fontFamily: ORBITRON,
                fontSize: '0.7rem',
                color: TEAL_DIM,
                cursor: 'pointer',
                letterSpacing: '0.1em',
              }}
            >
              GO HOME
            </button>
          </div>
        )}
      </div>
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 0.3; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1); }
        }
      `}</style>
    </div>
  );
}
